import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PieChart, BarChart3, TrendingUp, Download, FileText, Users, DollarSign } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
} from "recharts";

// Mock data for reports
const activationFunnel = [
  { stage: "Applied", count: 450 },
  { stage: "Documents Submitted", count: 420 },
  { stage: "Verified", count: 380 },
  { stage: "LMS Completed", count: 320 },
  { stage: "Active", count: 285 },
];

const earningsVsPerformance = [
  { performance: "60-70%", earnings: 2500 },
  { performance: "70-80%", earnings: 3800 },
  { performance: "80-90%", earnings: 5200 },
  { performance: "90-100%", earnings: 7500 },
];

const supplyDemand = [
  { region: "KL", supply: 85, demand: 100 },
  { region: "Selangor", supply: 120, demand: 95 },
  { region: "Penang", supply: 45, demand: 60 },
  { region: "Johor", supply: 55, demand: 70 },
  { region: "Others", supply: 80, demand: 75 },
];

const qualityRevenue = [
  { name: "High Quality (4.5+)", value: 65, color: "#10B981" },
  { name: "Good (3.5-4.5)", value: 25, color: "#F59E0B" },
  { name: "Needs Improvement (<3.5)", value: 10, color: "#EF4444" },
];

export default function AdminReports() {
  return (
    <div className="space-y-6" data-testid="admin-reports">
      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="kpi-card cursor-pointer hover:shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Activation Report</p>
                <p className="text-lg font-heading font-semibold text-slate-900">Funnel Analysis</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card cursor-pointer hover:shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Retention Report</p>
                <p className="text-lg font-heading font-semibold text-slate-900">Cohort Analysis</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card cursor-pointer hover:shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Financial Report</p>
                <p className="text-lg font-heading font-semibold text-slate-900">Earnings Analysis</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card cursor-pointer hover:shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Performance Report</p>
                <p className="text-lg font-heading font-semibold text-slate-900">Quality Metrics</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activation Funnel */}
      <Card className="chart-container">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg">Therapist Activation Funnel</CardTitle>
          <Button variant="outline" size="sm" data-testid="download-funnel-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={activationFunnel}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="stage" />
              <YAxis />
              <Tooltip 
                formatter={(value) => [value, 'Count']}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
              />
              <Bar dataKey="count" fill="#F40d07" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600">
              <strong>Insight:</strong> 63.3% conversion rate from application to active status. 
              Main drop-off occurs at LMS completion stage (15.8% drop).
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Earnings vs Performance */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Earnings vs Performance Correlation</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={earningsVsPerformance}>
                <defs>
                  <linearGradient id="earningsGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="performance" />
                <YAxis tickFormatter={(value) => `RM ${value}`} />
                <Tooltip 
                  formatter={(value) => [`RM ${value}`, 'Avg. Monthly']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="earnings" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  fill="url(#earningsGrad2)" 
                />
              </AreaChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-700">
                <strong>Insight:</strong> Strong positive correlation between performance score and earnings. 
                Top performers earn 3x more than average.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Quality vs Revenue */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Revenue by Quality Tier</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPie>
                  <Pie
                    data={qualityRevenue}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {qualityRevenue.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {qualityRevenue.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-xs text-slate-600">{item.name}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Insight:</strong> 65% of revenue comes from high-quality therapists (4.5+ rating).
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Supply vs Demand */}
      <Card className="chart-container">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-lg">Supply-Demand Balance by Region</CardTitle>
          <Badge className="bg-amber-100 text-amber-700">Penang: 25% undersupplied</Badge>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={supplyDemand}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="region" />
              <YAxis />
              <Tooltip 
                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
              />
              <Bar dataKey="supply" fill="#3B82F6" name="Supply" radius={[4, 4, 0, 0]} />
              <Bar dataKey="demand" fill="#F40d07" name="Demand" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
            {supplyDemand.map((region) => {
              const balance = region.supply - region.demand;
              return (
                <div key={region.region} className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="font-medium text-slate-900">{region.region}</p>
                  <p className={`text-sm ${balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {balance >= 0 ? `+${balance}` : balance} therapists
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Export */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Generate Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2" data-testid="export-therapist-report">
              <Users className="w-6 h-6" />
              <span className="text-sm">Therapist Report</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2" data-testid="export-financial-report">
              <DollarSign className="w-6 h-6" />
              <span className="text-sm">Financial Report</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2" data-testid="export-performance-report">
              <TrendingUp className="w-6 h-6" />
              <span className="text-sm">Performance Report</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2" data-testid="export-compliance-report">
              <BarChart3 className="w-6 h-6" />
              <span className="text-sm">Compliance Report</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
